<template>
  <div class="lot-live">
    <div class="lot-border-content">
      <span class="lot-status-badge" v-if="props.lotData.sold_status === LotSoldStatuses.SOLD">
        {{ trans("live.catalog.lot_card.sold") }}
      </span>
      <div class="lot-top-block">
        <div class="lot-number">
          {{ trans("live.catalog.lot_card.lot_number", { number: props.lotData.lot_number }) }}
        </div>
        <div v-if="auctionStore.user?.id" class="lot-icons">
          <div
            v-if="isShowAlert"
            :class="['notify-block', { active: lotData.alert_enabled }]"
            @click.stop.prevent="openLotAlertSetting">
            <base-icon icon-name="bell" width="19" height="19" view-box="0 0 19 19">
              <bell-icon></bell-icon>
            </base-icon>
            <base-icon icon-name="bell-fill" width="16" height="20" view-box="0 0 16 20">
              <bell-fill-icon></bell-fill-icon>
            </base-icon>
          </div>
          <favorite-button v-if="props.lotData?.id" :lot-id-prop="props.lotData.id"></favorite-button>
        </div>
      </div>
      <div class="lot-image">
        <img
          class="img loading-transparent-alt"
          :src="props.lotData?.main_image"
          loading="lazy"
          :alt="props.lotData?.title"
          width="113"
          height="127"
          @error="eventAfterFallbackLoadImg"
        />
        <div v-if="note" class="lot-card-note">
          <div
            v-if="catalogPopup && note"
            :class="['lot-bottom', { live: isLive, 'catalog-popup on-note': catalogPopup }]">
            {{ buttonText }}
          </div>
          <div class="note" ref="noteRef">{{ note }}</div>
        </div>
        <div v-if="catalogPopup && !note" :class="['lot-bottom', { live: isLive, 'catalog-popup': catalogPopup }]">
          {{ buttonText }}
        </div>
      </div>
      <div v-if="title && props.withTitle" class="lot-title">{{ title }}</div>
      <div v-if="!catalogPopup" :class="['lot-bottom', { live: isLive }]">
        {{ buttonText }}
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import FavoriteButton from "@/components/_partials/FavoriteButton.vue";
  import { computed, defineProps, inject, onMounted, onUnmounted, ref } from "vue";
  import { ILot } from "@/models";
  import { LotStatuses, LotSoldStatuses } from "@/enum";
  import { trans } from "@/utils/trans";
  import { format_currency } from "@/utils/currencies";
  import { useAuctionStore } from "@/stores/AuctionStore";
  import apiClient from "@/services/api";
  import { useToast } from "primevue/usetoast";
  import { createTooltip, destroyTooltip } from "floating-vue";
  import { BaseIcon, BellIcon } from "@/components/icons";
  import BellFillIcon from "@/components/icons/BellFillIcon.vue";
  const emitter = inject("emitter");
  const toast = useToast();

  const props = withDefaults(
    defineProps<{
      lotData: ILot;
      withTitle?: boolean;
      catalogPopup?: boolean;
    }>(),
    {
      withTitle: false,
      catalogPopup: false
    }
  );

  const auctionStore = useAuctionStore();
  const noteRef = ref<HTMLDivElement>();

  const isLive = computed(() => props.lotData.status === LotStatuses.LIVE);
  const isShowAlert = computed(() => props.lotData.sold_status === LotSoldStatuses.PREPARED && !isLive.value);
  const title = computed(() => props.lotData?.title);

  const note = computed(() => {
    let result = null;
    if (auctionStore.user?.id && !auctionStore.user?.is_admin) {
      result = props.lotData.note?.length ? props.lotData.note : null;
    } else if (auctionStore.user?.is_admin) {
      result = props.lotData.admin_notes?.length ? props.lotData.admin_notes : null;
    }
    return result;
  });

  const buttonText = computed(() => {
    if (isLive.value) {
      return trans("live.catalog.lot_card.is_live");
    } else {
      return format_currency(props.lotData?.current_bid);
    }
  });

  onMounted(() => {
    if (noteRef.value) {
      createTooltip(
        noteRef.value,
        {
          triggers: ["hover", "touch"],
          theme: "info-tooltip",
          placement: "top",
          distance: 10,
          skidding: 10,
          popperClass: "fit-content",
          content: `<div class="info-tooltip wide">${note.value}</div>`,
          html: true
        },
        null
      );
    }
  });

  onUnmounted(() => {
    if (noteRef.value) {
      destroyTooltip(noteRef.value);
    }
  });

  const openLotAlertSetting = () => {
    if (props.lotData.alert_enabled) {
      apiClient.delete(`/live/lots/${props.lotData.id}/alert`).then(() => {
        auctionStore.$patch((storeState) => {
          let lotIndex = storeState.lots.findIndex((lot) => lot.id === props.lotData.id);
          if (lotIndex >= 0) {
            storeState.lots[lotIndex].alert_enabled = false;
          }
          lotIndex = storeState.catalog?.searchLots?.findIndex((lot) => lot.id === props.lotData.id);
          if (lotIndex >= 0) {
            storeState.catalog.searchLots[lotIndex].alert_enabled = false;
          }
        });

        toast.add({
          severity: "success",
          summary: trans("live.toast.success"),
          detail: trans("live.toast.live_lot_alert.disable_success"),
          life: 6000
        });
      });
    } else {
      apiClient.get(`/live/lots/${props.lotData.id}/alert`).then((response) => {
        emitter?.emit("on:live-lot-alerts:modal-show", {
          title: `${props.lotData?.lot_number} ${props.lotData?.title}`,
          lotId: response.data.lot_id,
          settings: response.data.settings
        });
      });
    }
  };

  const eventAfterFallbackLoadImg = (event: Event) => {
    event.target?.classList.remove("loading-transparent-alt");
  }
</script>
